import React, { useEffect, useState } from 'react';
import { Gift, TrendingUp, DollarSign, Star, AlertCircle, Calendar, ChevronDown } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
// Re-cableado a lealify-base (/analytics/rewards-performance) — reemplaza Elasticsearch.
import { analyticsApi } from '../../../api/analyticsApi';
import { RUTA_CAMPANAS, prefillPromoPremio } from '../../analytics/utils/campaignActions';
import CountUp from '../../../components/ui/CountUp';
import BrandLoader from '../../../components/common/BrandLoader';

// Detecta si el documento está en modo oscuro para teñir las gráficas de Recharts.
const isDarkMode = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

const RewardsPerformancePanel: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Estado para el selector de tiempo
  // 30 = 30 días, 10000 = Todos los tiempos (según lógica en backend)
  const [timeRange, setTimeRange] = useState<number>(30);

  // Detectar modo: 'visits' (puntos) o 'cashback' (dinero)
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  useEffect(() => {
    const loadData = async () => {
      if (!currentUser?.businessId) return;
      try {
        setLoading(true);
        // Solicitamos las analíticas con el rango seleccionado
        const result = await analyticsApi.rewardsPerformance(timeRange);
        setData(result);
      } catch (error) {
        console.error("Error loading reward analytics", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser?.businessId, timeRange]); // Recargar si cambia el negocio o el rango

  // Renderizado de carga inicial (esqueleto)
  if (loading && !data) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line h-64 flex items-center justify-center">
         <div className="flex flex-col items-center text-content-3">
            <BrandLoader size={36} />
            <span className="text-sm">Calculando estadísticas...</span>
         </div>
      </div>
    );
  }
  
  // Si no hay datos y terminó de cargar
  if (!data && !loading) return null;

  // Transición suave de opacidad mientras se recargan datos nuevos al cambiar el filtro
  const containerOpacity = loading ? 'opacity-50 pointer-events-none' : 'opacity-100';
  
  // Valores seguros por defecto
  const { totalRedemptions, totalPointsSpent, topRewards } = data || { totalRedemptions: 0, totalPointsSpent: 0, topRewards: [] };
  // Tendencia de canjes por día (el backend ya la devuelve; antes se ignoraba).
  const trend: Array<{ date: string; count: number }> = Array.isArray(data?.trend) ? data.trend : [];
  const dark = isDarkMode();
  const gridColor = dark ? '#334155' : '#e5e7eb';
  // Morado de la serie: más claro en dark para que no se funda con el fondo
  const areaStroke = dark ? '#c4b5fd' : '#8b5cf6';
  const areaFill = dark ? '#c4b5fd' : '#a78bfa';
  const axisColor = dark ? '#94a3b8' : '#6b7280';

  return (
    <div className="bg-surface shadow rounded-lg p-6 border border-line animate-fade-in-up relative">
      
      {/* Header con Título y Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h3 className="text-lg font-medium text-content flex items-center">
            <Gift className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
            Rendimiento de Recompensas
        </h3>

        {/* Selector de Rango de Tiempo */}
        <div className="relative inline-block text-left">
            <div className="flex items-center bg-surface-2 border border-line rounded-md px-3 py-2 hover:bg-canvas transition-colors">
                <Calendar className="w-4 h-4 text-content-3 mr-2" />
                <select 
                    value={timeRange}
                    onChange={(e) => setTimeRange(Number(e.target.value))}
                    className="bg-transparent border-none text-sm font-medium text-content-2 focus:ring-0 cursor-pointer pr-8 appearance-none"
                    style={{ outline: 'none', minWidth: '140px' }}
                >
                    <option value={30}>Últimos 30 días</option>
                    <option value={90}>Últimos 3 meses</option>
                    <option value={365}>Último año</option>
                    <option value={10000}>Todos los tiempos</option>
                </select>
                <ChevronDown className="w-4 h-4 text-content-3 absolute right-3 pointer-events-none" />
            </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-opacity duration-300 ${containerOpacity}`}>
        
        {/* KPI 1: Total Canjes */}
        <div className="flex items-center p-4 bg-purple-50 dark:bg-purple-500/10 rounded-lg border border-purple-100 dark:border-purple-500/30">
          <div className="p-3 rounded-full bg-surface shadow-sm mr-4">
            <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-content-3">Canjes Realizados</p>
            <p className="text-2xl font-display font-bold text-content"><CountUp value={totalRedemptions} /></p>
          </div>
        </div>

        {/* KPI 2: Costo / Puntos */}
        <div className="flex items-center p-4 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-100 dark:border-blue-500/30">
          <div className="p-3 rounded-full bg-surface shadow-sm mr-4">
             {loyaltyMode === 'cashback' ? <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" /> : <Star className="w-6 h-6 text-yellow-500" />}
          </div>
          <div>
            <p className="text-sm font-medium text-content-3">
              {loyaltyMode === 'cashback' ? 'Dinero Retornado' : 'Puntos Canjeados'}
            </p>
            <p className="text-2xl font-display font-bold text-content">
              <CountUp
                value={totalPointsSpent}
                format={(n) => loyaltyMode === 'cashback' ? `$${n.toFixed(2)}` : `${Math.floor(n)}`}
              />
            </p>
          </div>
        </div>

        {/* Gráfico de Barras: Top Recompensas */}
        <div className="md:col-span-1 border-l border-line pl-0 md:pl-8 mt-4 md:mt-0 pt-4 md:pt-0 border-t md:border-t-0">
          <h4 className="text-sm font-medium text-content-2 mb-3 flex items-center justify-between">
              <span>Top Recompensas</span>
              {topRewards.length > 0 && <span className="text-xs text-content-3">Cant.</span>}
          </h4>
          
          <div className="space-y-3">
            {topRewards.length === 0 ? (
               <div className="text-sm text-content-3 flex items-center justify-center py-8 bg-surface-2 rounded-lg border border-dashed border-line">
                   <AlertCircle className="w-4 h-4 mr-2"/> Sin datos en este periodo
               </div>
            ) : (
                topRewards.map((reward: any, idx: number) => {
                  // Calcular porcentaje relativo al mayor valor para la barra
                  const maxVal = topRewards[0]?.count || 1;
                  const percent = Math.max(5, (reward.count / maxVal) * 100); // Mínimo 5% visual
                  
                  return (
                    <div key={idx} className="group">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-content-2 truncate max-w-[160px]" title={reward.title}>
                            {idx + 1}. {reward.title}
                        </span>
                        <span className="text-content-3 font-medium whitespace-nowrap">
                            {reward.count} {reward.count === 1 ? 'canje' : 'canjes'}
                            {reward.points ? <span className="text-content-3/70"> · {Math.round(reward.points)} pts</span> : null}
                        </span>
                      </div>
                      <div className="w-full bg-canvas rounded-full h-1.5 overflow-hidden">
                        <div 
                          className="bg-purple-500 h-1.5 rounded-full transition-[width] duration-300 ease-out group-hover:bg-purple-600" 
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

      </div>

      {/* Acción: promocionar el premio estrella con una campaña */}
      {topRewards.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => navigate(RUTA_CAMPANAS, { state: { prefill: prefillPromoPremio(topRewards[0].title) } })}
            className="w-full inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Megaphone className="h-4 w-4" /> Promocionar «{topRewards[0].title}» con una campaña
          </button>
        </div>
      )}

      {/* Tendencia de canjes en el tiempo (el dato ya venía del backend; antes se descartaba) */}
      {trend.length > 1 && (
        <div className={`mt-8 pt-6 border-t border-line transition-opacity duration-300 ${containerOpacity}`}>
          <h4 className="text-sm font-medium text-content-2 mb-3 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-purple-500" /> Canjes en el tiempo
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={trend} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="canjesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={areaFill} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={areaFill} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={{ stroke: gridColor }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} width={32} />
              <Tooltip
                contentStyle={{ background: dark ? '#1e293b' : '#fff', border: `1px solid ${gridColor}`, borderRadius: 8, fontSize: 12, color: dark ? '#f1f5f9' : '#111827' }}
                labelStyle={{ color: axisColor }}
                formatter={(v: any) => [`${v} canjes`, '']}
              />
              <Area type="monotone" dataKey="count" stroke={areaStroke} strokeWidth={2} fill="url(#canjesGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default RewardsPerformancePanel;